import { Box } from '@components/ui/atoms/Box';
import { Flex } from '@components/ui/atoms/Flex';
import { Text } from '@components/ui/atoms/Text';
import { formatAmount } from '@shared/utils';

import type { Notification } from '../../NotificationModal/types';

interface NotificationDetailProps {
  notification: Notification | null;
}

export const NotificationDetail: React.FC<NotificationDetailProps> = ({ notification }) => {
  if (!notification) {
    return (
      <Flex
        flex="1"
        alignItems="center"
        justifyContent="center"
        bg="white/60"
        borderRadius="8px"
        m="24px"
      >
        <Text fontSize="16px" color="gray.400">
          Select a notification to view details
        </Text>
      </Flex>
    );
  }

  // System notification - simple text display
  if (notification.type === 'system' || notification.type === 'dispute') {
    return (
      <Box flex="1">
        <Flex
          direction="column"
          gap="24px"
          bg="white/60"
          borderRadius="12px"
          fontSize="xs"
          py={6}
          maxW="800px"
        >
          <Flex
            justifyContent="space-between"
            alignItems="center"
            px={5}
            pb={4}
            borderBottom="1px solid"
            borderColor="body.550/10"
          >
            <Text fontSize="md" fontWeight="600" color="title.dark">
              {notification.title}
            </Text>
            <Text>{notification.timeAgo}</Text>
          </Flex>
          <Flex direction="column" gap="12px" px={6}>
            <Text fontSize="sm">{notification.message}</Text>
          </Flex>
        </Flex>
      </Box>
    );
  }

  // Default - show full invoice details for all other types
  if (!notification.details) {
    return (
      <Flex flex="1" alignItems="center" justifyContent="center" bg="white/60" borderRadius="8px">
        <Flex direction="column" alignItems="center" gap="16px">
          <Text fontSize="18px" fontWeight="600" color="title.dark">
            {notification.title}
          </Text>
          <Text fontSize="14px" color="gray.600" textAlign="center">
            {notification.message} {notification.amount}
          </Text>
          <Text fontSize="12px" color="gray.400">
            {notification.timeAgo}
          </Text>
        </Flex>
      </Flex>
    );
  }

  const { details } = notification;

  return (
    <Box flex="1">
      <Flex
        direction="column"
        gap="24px"
        bg="white/60"
        borderRadius="12px"
        fontSize="xs"
        py={6}
        maxW="800px"
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          px={5}
          pb={4}
          borderBottom="1px solid"
          borderColor="body.550/10"
        >
          <Text fontSize="md" fontWeight="600" color="title.dark">
            {notification.title}
          </Text>
          <Text fontSize="xs" color="body.dark">
            {details.createdDate}
          </Text>
        </Flex>

        <Text fontSize="lg" fontWeight="700" color="title.dark" px={6}>
          Invoice
        </Text>

        <Flex direction="column" gap="8px" px={6}>
          <Flex gap="8px">
            <Text color="body.dark" fontWeight="500">
              INVOICE ID:
            </Text>
            <Text color="title.dark" fontWeight="600">
              {details.invoiceId}
            </Text>
          </Flex>
          <Flex gap="8px">
            <Text color="body.dark" fontWeight="500">
              CREATED:
            </Text>
            <Text color="title.dark">{details.createdDate}</Text>
          </Flex>
          <Flex gap="8px">
            <Text color="body.dark" fontWeight="500">
              DUE:
            </Text>
            <Text color="title.dark">{details.dueDate}</Text>
          </Flex>
          <Flex gap="8px">
            <Text color="body.dark" fontWeight="500">
              INTERNAL INVOICE ID:
            </Text>
            <Text color="title.dark">{details.internalInvoiceId}</Text>
          </Flex>
        </Flex>

        <Flex gap="80px" px={6}>
          {details.payTo && (
            <Flex direction="column" gap="12px">
              <Text color="title.dark" textTransform="uppercase">
                Pay To
              </Text>
              <Flex direction="column" gap="4px">
                <Text color="title.dark" fontWeight="500">
                  {details.payTo.name}
                </Text>
                <Text color="brand">{details.payTo.email}</Text>
                {details.payTo.phone && <Text color="body.dark">{details.payTo.phone}</Text>}
              </Flex>
            </Flex>
          )}

          {details.billTo && (
            <Flex direction="column" gap="12px">
              <Text color="title.dark" textTransform="uppercase">
                Bill To
              </Text>
              <Flex direction="column" gap="4px">
                <Text color="title.dark" fontWeight="500">
                  {details.billTo.name}
                </Text>
                <Text color="brand">{details.billTo.email}</Text>
              </Flex>
            </Flex>
          )}
        </Flex>

        {/* Invoice Table */}
        {details.items && details.items.length > 0 && (
          <Box px={6}>
            <Flex
              p="12px 16px"
              borderTopRadius="8px"
              borderBottom="1px solid"
              borderColor="body.550/10"
            >
              <Text flex="2" fontSize="14px" fontWeight="600" color="title.dark">
                Item
              </Text>
              <Text flex="1" fontSize="14px" fontWeight="600" color="title.dark" textAlign="right">
                Qty
              </Text>
              <Text flex="1" fontSize="14px" fontWeight="600" color="title.dark" textAlign="right">
                Unit Price
              </Text>
              <Text flex="1" fontSize="14px" fontWeight="600" color="title.dark" textAlign="right">
                Amount
              </Text>
            </Flex>
            {details.items.map((item, index) => (
              <Flex
                key={index}
                p="12px 16px"
                borderBottom="1px solid"
                borderColor="body.550/10"
                _last={{ borderBottom: 'none' }}
              >
                <Text flex="2" fontSize="14px" color="title.dark">
                  {item.name}
                </Text>
                <Text flex="1" fontSize="14px" color="title.dark" textAlign="right">
                  {item.quantity}
                </Text>
                <Text flex="1" fontSize="14px" color="title.dark" textAlign="right">
                  {item.unitPrice.toFixed(2)}
                </Text>
                <Text flex="1" fontSize="14px" color="title.dark" textAlign="right">
                  {item.amount.toFixed(2)}
                </Text>
              </Flex>
            ))}
          </Box>
        )}

        {/* Totals */}
        <Flex direction="column" gap="12px" alignItems="flex-end" fontSize="sm" pt="16px" px={6}>
          {details.subtotal !== undefined && (
            <Flex gap="60px" minW="300px">
              <Text color="gray.600">Sub Total</Text>
              <Text color="title.dark" fontWeight="500" ml="auto">
                {formatAmount(details.subtotal)}
              </Text>
            </Flex>
          )}
          {details.processingFee !== undefined && (
            <Flex gap="60px" minW="300px">
              <Text color="gray.600">Processing Fee</Text>
              <Text color="title.dark" fontWeight="500" ml="auto">
                {formatAmount(details.processingFee)}
              </Text>
            </Flex>
          )}
          {details.total !== undefined && (
            <Flex gap="60px" minW="300px" pt="12px" borderTop="1px solid" borderColor="body.550/10">
              <Text color="title.dark" fontWeight="600">
                Amount Due
              </Text>
              <Text color="title.dark" fontWeight="700" ml="auto">
                {formatAmount(details.total)}
              </Text>
            </Flex>
          )}
        </Flex>

        {/* Footer */}
        <Flex justifyContent="center" pt="24px" mx="auto">
          <Text fontSize="14px" color="gray.600">
            Thank you for your business!
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
};
